import React, { useState } from 'react';
import { Mail, MessageSquare, Send } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;

// Small loading spinner for buttons that matches the LoadingScreen style
const LoadingSpinner = ({ size = 5, className = '' }) => (
  <div className={`w-${size} h-${size} border-2 border-t-transparent border-white rounded-full animate-spin mr-2 ${className}`}></div>
);

export default function ContactUs({ className = '' }) {
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [loading, setLoading] = useState(false);

  const validate = () => {
    let valid = true;
    setEmailError('');
    setMessageError('');
    setGeneralError('');

    if (!email) {
      setEmailError('Email is required');
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      valid = false;
    }

    if (!message.trim()) {
      setMessageError('Message is required');
      valid = false;
    }

    // Focus on the first field with an error
    if (!valid) {
      const firstErrorField = !email ? 'email' : 'message';
      document.getElementById(firstErrorField)?.focus();
    }

    return valid;
  };

  const handleSubmit = async () => {
    if (loading) return;
    setStatus(null);
    if (!validate()) return;
    setLoading(true);
    setGeneralError('');
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        body: JSON.stringify({ email, message }),
      });
      if (res.status === 200) {
        setStatus('success');
        setEmail('');
        setMessage('');
      } else if (res.status === 429) {
        setGeneralError('Too many submissions. Please wait a few seconds and try again.');
      } else if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        const errs = data.errors || {};
        if (errs.Email) setEmailError(errs.Email[0]);
        if (errs.Message) setMessageError(errs.Message[0]);
      } else {
        setGeneralError('An unexpected error occurred. Please try again.');
      }
    } catch (e) {
      console.error(e);
      setGeneralError('Unable to connect. Check your network and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="px-6 py-3 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded-full hover:bg-[var(--hover-dark)] transition"
        >
          Contact Us
        </button>
      ) : status === 'success' ? (
        <div className="bg-[var(--accent-primary)] bg-opacity-10 rounded-lg p-6">
          <div className="flex justify-center mb-4">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM19.6 8.25L12.53 12.67C12.21 12.87 11.79 12.87 11.47 12.67L4.4 8.25C4.15 8.09 4 7.82 4 7.53C4 6.86 4.73 6.46 5.3 6.81L12 11L18.7 6.81C19.27 6.46 20 6.86 20 7.53C20 7.82 19.85 8.09 19.6 8.25Z" fill="var(--accent-primary)"/>
            </svg>
          </div>
          <p className="text-[var(--accent-secondary)] text-center mb-2 font-medium">
            Message sent successfully
          </p>
          <p className="text-[var(--text-secondary)] text-sm text-center">
            Thank you for contacting us. We'll get back to you soon.
          </p>
        </div>
      ) : (
        <div className={`space-y-4 ${loading ? 'opacity-50' : ''}`}>
          {generalError && <p className="text-red-500 text-sm">{generalError}</p>}
          
          <div>
            <div className="relative">
              <input
                type="email"
                id="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setStatus(null); setEmailError(''); }}
                disabled={loading}
                className={`w-full p-2 pl-10 border rounded focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] ${
                  emailError ? 'border-red-500 focus:ring-red-500' : 'border-[var(--border-color)]'
                } bg-white text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] placeholder:opacity-50`}
                placeholder="Enter your email"
              />
              <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {emailError && <p className="mt-1 text-red-500 text-sm">{emailError}</p>}
          </div>

          <div>
            <div className="relative">
              <textarea
                id="message"
                value={message}
                onChange={e => { setMessage(e.target.value); setStatus(null); setMessageError(''); }}
                rows={6}
                disabled={loading}
                className={`w-full p-2 pl-10 border rounded focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] ${
                  messageError ? 'border-red-500 focus:ring-red-500' : 'border-[var(--border-color)]'
                } bg-white resize-y text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] placeholder:opacity-50`}
                placeholder="Your message..."
              />
              <MessageSquare className="w-5 h-5 absolute left-3 top-3 text-[var(--text-secondary)]" />
            </div>
            {messageError && <p className="mt-1 text-red-500 text-sm">{messageError}</p>}
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] transition disabled:opacity-50"
          >
            {loading ? (
              <>
                <LoadingSpinner />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
